import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Save, Sparkles, Wand2, History, Undo2, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings as SettingsApi, useAppSettings } from "@/lib/appSettings";

/**
 * 3 secções para admin:
 *   <BrandingPanel />        (logo, nome, tagline, primary_color, header toggle)
 *   <ContentPanel />         (chaves textuais com edit inline + save batch)
 *   <AiControlPanel />       (prompt → preview → apply + history + rollback)
 */

const BRAND_KEYS = [
  "app.brand.logo_url",
  "app.brand.name",
  "app.brand.tagline",
  "app.brand.primary_color",
  "app.header.visible",
];

const CONTENT_KEYS = [
  "dashboard.hero.eyebrow",
  "dashboard.hero.title",
  "dashboard.hero.subtitle",
  "dashboard.cta.new_assessment",
  "dashboard.cta.new_patient",
  "dashboard.stats.patients",
  "dashboard.stats.assessments",
  "dashboard.stats.upcoming",
  "dashboard.shortcuts",
  "settings.header.title",
  "reports.header.title",
  "footer.copyright",
  "plan.section.title",
  "pdf.cover.title",
];

function FieldByType({ k, type, value, onChange }) {
  if (type === "bool") {
    return (
      <Switch
        data-testid={`cms-input-${k}`}
        checked={!!value}
        onCheckedChange={onChange}
      />
    );
  }
  if (type === "color") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="color"
          data-testid={`cms-input-${k}`}
          value={value || "#38bdf8"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-md cursor-pointer bg-transparent border border-slate-700"
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#38bdf8"
          className="bg-slate-900 border-slate-800 text-slate-50 max-w-[7rem] font-mono text-xs"
        />
      </div>
    );
  }
  if (type === "image") {
    return (
      <div className="flex items-center gap-3">
        {value && <img src={value} alt="logo" className="w-10 h-10 rounded bg-white object-contain border border-slate-700" />}
        <label className="cursor-pointer text-xs px-3 py-2 rounded-full border border-slate-700 text-slate-200 hover:bg-slate-800">
          <input
            type="file" accept="image/*" className="hidden"
            data-testid={`cms-input-${k}`}
            onChange={(e) => {
              const f = e.target.files?.[0]; if (!f) return;
              if (f.size > 2 * 1024 * 1024) { toast.error("Logo máximo 2MB"); return; }
              const reader = new FileReader();
              reader.onload = (ev) => onChange(ev.target.result);
              reader.readAsDataURL(f);
              e.target.value = "";
            }}
          />
          Carregar imagem
        </label>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs text-rose-400 hover:text-rose-300">
            Remover
          </button>
        )}
      </div>
    );
  }
  if (type === "longtext") {
    return (
      <Textarea
        data-testid={`cms-input-${k}`}
        value={value || ""}
        rows={3}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-900 border-slate-800 text-slate-50"
      />
    );
  }
  if (type === "json") {
    const text = typeof value === "string" ? value : JSON.stringify(value || [], null, 2);
    let isValid = true;
    try { JSON.parse(text); } catch { isValid = false; }
    return (
      <div>
        <Textarea
          data-testid={`cms-input-${k}`}
          value={text}
          rows={6}
          onChange={(e) => {
            try { onChange(JSON.parse(e.target.value)); }
            catch { onChange(e.target.value); }
          }}
          className={`bg-slate-900 border-slate-800 text-slate-50 font-mono text-xs ${!isValid ? "border-rose-500/50" : ""}`}
          spellCheck={false}
        />
        {!isValid && <div className="text-[10px] text-rose-300 mt-1">JSON inválido — corrige antes de guardar.</div>}
      </div>
    );
  }
  return (
    <Input
      data-testid={`cms-input-${k}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="bg-slate-900 border-slate-800 text-slate-50"
    />
  );
}

export function BrandingPanel() {
  const { values, catalog, refresh } = useAppSettings();
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d = {};
    BRAND_KEYS.forEach((k) => { d[k] = values[k]; });
    setDraft(d);
  }, [values]);

  const save = async () => {
    setBusy(true);
    try {
      const items = BRAND_KEYS
        .filter((k) => draft[k] !== values[k])
        .map((k) => ({ key: k, value: draft[k] }));
      if (items.length === 0) { toast.message("Sem alterações"); return; }
      await SettingsApi.adminApply(items, "manual");
      await refresh();
      toast.success(`Branding atualizado (${items.length} chaves)`);
    } catch { toast.error("Não foi possível guardar"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="branding-panel" className="space-y-5">
      <div className="rounded-2xl border border-sky-500/20 bg-slate-900/50 p-6 space-y-5">
        <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Branding global</div>
        {BRAND_KEYS.map((k) => {
          const meta = catalog[k] || { type: "text" };
          return (
            <div key={k} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <Label className="text-slate-300 text-sm">
                <div>{prettyLabel(k)}</div>
                <div className="text-[10px] text-slate-500 font-normal">{meta.description || k}</div>
              </Label>
              <div className="md:col-span-2">
                <FieldByType k={k} type={meta.type} value={draft[k]} onChange={(v) => setDraft({ ...draft, [k]: v })} />
              </div>
            </div>
          );
        })}
        <div className="flex justify-end">
          <Button data-testid="branding-save-btn" onClick={save} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6">
            <Save className="w-4 h-4 mr-2" /> {busy ? "A guardar…" : "Guardar branding"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ContentPanel() {
  const { values, catalog, refresh } = useAppSettings();
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const d = {};
    CONTENT_KEYS.forEach((k) => { d[k] = values[k]; });
    setDraft(d);
  }, [values]);

  const dirtyCount = useMemo(
    () => CONTENT_KEYS.filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(values[k])).length,
    [draft, values],
  );

  const filteredKeys = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return CONTENT_KEYS;
    return CONTENT_KEYS.filter((k) =>
      k.toLowerCase().includes(s) ||
      String(catalog[k]?.default || "").toLowerCase().includes(s) ||
      String(values[k] || "").toLowerCase().includes(s),
    );
  }, [search, catalog, values]);

  const save = async () => {
    setBusy(true);
    try {
      const items = CONTENT_KEYS
        .filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(values[k]))
        .map((k) => ({ key: k, value: draft[k] }));
      if (items.length === 0) { toast.message("Sem alterações"); return; }
      await SettingsApi.adminApply(items, "manual");
      await refresh();
      toast.success(`Conteúdo atualizado (${items.length} chaves)`);
    } catch { toast.error("Não foi possível guardar"); }
    finally { setBusy(false); }
  };

  const reset = (k) => setDraft({ ...draft, [k]: catalog[k]?.default ?? "" });

  return (
    <div data-testid="content-panel" className="space-y-5">
      <div className="rounded-2xl border border-emerald-500/20 bg-slate-900/50 p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold">Conteúdo da UI</div>
          <span className="text-[11px] text-slate-500">{dirtyCount} alteração(ões) por guardar</span>
        </div>
        <Input
          data-testid="content-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar chave, valor atual ou default…"
          className="bg-slate-950 border-slate-800 text-slate-50"
        />
        {filteredKeys.length === 0 && <div className="text-sm text-slate-500">Sem resultados.</div>}
        {filteredKeys.map((k) => {
          const meta = catalog[k] || { type: "text", default: "" };
          return (
            <div key={k} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <Label className="text-slate-300 text-sm pt-2">
                <div className="font-mono text-[11px] text-slate-400">{k}</div>
                <button type="button" onClick={() => reset(k)} className="text-[10px] text-slate-500 hover:text-sky-300 underline mt-1">repor default</button>
              </Label>
              <div className="md:col-span-2">
                <FieldByType k={k} type={meta.type} value={draft[k]} onChange={(v) => setDraft({ ...draft, [k]: v })} />
                <div className="text-[10px] text-slate-600 mt-1 font-mono truncate">default: {typeof meta.default === "object" ? JSON.stringify(meta.default) : String(meta.default ?? "")}</div>
              </div>
            </div>
          );
        })}
        <div className="flex justify-end">
          <Button data-testid="content-save-btn" onClick={save} disabled={busy || dirtyCount === 0} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full px-6">
            <Save className="w-4 h-4 mr-2" /> {busy ? "A guardar…" : `Guardar (${dirtyCount})`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AiControlPanel() {
  const { refresh, catalog, values } = useAppSettings();
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {preview: [...], notes, prompt}
  const [history, setHistory] = useState([]);
  const [applying, setApplying] = useState(false);
  const [rollingBack, setRollingBack] = useState("");

  const loadHistory = async () => {
    try { setHistory(await SettingsApi.adminHistory()); } catch { /* */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const runAi = async () => {
    if (!prompt.trim()) { toast.error("Escreve um prompt"); return; }
    setBusy(true); setPreview(null);
    try {
      const res = await SettingsApi.adminAiSuggest(prompt.trim());
      if (!res.preview?.length) {
        toast.message("A IA não sugeriu alterações", { description: res.notes || "Tenta um prompt mais específico." });
      } else {
        setPreview(res);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao chamar a IA");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview?.preview?.length) return;
    setApplying(true);
    try {
      const items = preview.preview.map((p) => ({ key: p.key, value: p.suggested }));
      await SettingsApi.adminApply(items, "ai", preview.prompt);
      await refresh();
      await loadHistory();
      toast.success(`IA aplicou ${items.length} alteração(ões)`);
      setPreview(null); setPrompt("");
    } catch { toast.error("Não foi possível aplicar"); }
    finally { setApplying(false); }
  };

  const rollback = async (batch_id) => {
    if (!window.confirm("Reverter este batch?")) return;
    setRollingBack(batch_id);
    try {
      await SettingsApi.adminRollback(batch_id);
      await refresh();
      await loadHistory();
      toast.success("Batch revertido");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha no rollback"); }
    finally { setRollingBack(""); }
  };

  return (
    <div data-testid="ai-control-panel" className="space-y-5">
      {/* Prompt */}
      <div className="rounded-2xl border border-violet-500/20 bg-slate-900/50 p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-violet-300 font-semibold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Controlo IA
        </div>
        <p className="text-[12px] text-slate-400 mt-2 mb-3">
          Descreve o que queres alterar na UI. A IA gera sugestões para as chaves do catálogo (PT-PT, linguagem não-diagnóstica). Vais poder rever e aplicar antes de tomar efeito.
        </p>
        <Textarea
          data-testid="ai-prompt-input"
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder='Ex.: "Torna o hero do painel mais empático e curto" ou "Reformula as estatísticas para clínica desportiva"'
          className="bg-slate-950 border-slate-800 text-slate-50"
        />
        <div className="mt-3 flex justify-end">
          <Button data-testid="ai-run-btn" onClick={runAi} disabled={busy} className="bg-violet-500 hover:bg-violet-400 text-slate-950 rounded-full px-5">
            <Wand2 className="w-4 h-4 mr-2" /> {busy ? "A pensar…" : "Gerar sugestões"}
          </Button>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div data-testid="ai-preview" className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 font-semibold flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Pré-visualização ({preview.preview.length})
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setPreview(null)} className="text-slate-400">Descartar</Button>
              <Button data-testid="ai-apply-btn" onClick={apply} disabled={applying} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full">
                <Save className="w-4 h-4 mr-2" /> {applying ? "A aplicar…" : "Aplicar tudo"}
              </Button>
            </div>
          </div>
          {preview.notes && <p className="text-[12px] text-violet-200 italic">"{preview.notes}"</p>}
          <div className="space-y-2">
            {preview.preview.map((row) => (
              <div key={row.key} data-testid={`ai-row-${row.key}`} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-3 font-mono text-[11px] text-slate-400 break-all">{row.key}</div>
                <div className="md:col-span-4">
                  <div className="text-[10px] uppercase text-slate-500 mb-1">Atual</div>
                  <div className="text-xs text-slate-300 whitespace-pre-line break-words">{String(row.current ?? "")}</div>
                </div>
                <div className="md:col-span-5">
                  <div className="text-[10px] uppercase text-emerald-400 mb-1">Sugestão</div>
                  <div className="text-xs text-emerald-200 whitespace-pre-line break-words">{String(row.suggested ?? "")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-2">
          <History className="w-3.5 h-3.5" /> Histórico (últimos 50)
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500 mt-3">Sem alterações registadas.</div>
        ) : (
          <div className="space-y-2 mt-4">
            {history.map((h) => (
              <div key={h.batch_id} data-testid={`history-${h.batch_id}`} className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] text-slate-200 flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${h.source === "ai" ? "bg-violet-500/10 text-violet-300 border border-violet-500/30" : h.source === "rollback" ? "bg-amber-500/10 text-amber-300 border border-amber-500/30" : "bg-slate-700/30 text-slate-300 border border-slate-700"}`}>{h.source}</span>
                      <span>{(h.snapshots || []).length} chave(s)</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-500 text-[11px] font-mono">{new Date(h.applied_at).toLocaleString("pt-PT")}</span>
                      {h.rolled_back && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-300 border border-rose-500/30">revertido</span>}
                    </div>
                    {h.prompt && <div className="text-[11px] text-slate-500 italic mt-1 truncate">"{h.prompt}"</div>}
                  </div>
                  {!h.rolled_back && h.source !== "rollback" && (
                    <button
                      data-testid={`rollback-${h.batch_id}`}
                      onClick={() => rollback(h.batch_id)}
                      disabled={rollingBack === h.batch_id}
                      className="text-[11px] inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50"
                    >
                      <Undo2 className="w-3 h-3" /> {rollingBack === h.batch_id ? "…" : "Reverter"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function prettyLabel(key) {
  return key
    .replace(/^app\.brand\./, "")
    .replace(/^app\./, "")
    .replace(/_/g, " ")
    .replace(/\./g, " · ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
